import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router';
import './styles.css';
import { Layout } from './components/Layout.tsx';
import { SessionProvider, useSession } from './lib/session.tsx';
import { AlertsPage } from './pages/AlertsPage.tsx';
import { ChannelsPage } from './pages/ChannelsPage.tsx';
import { LoginPage } from './pages/LoginPage.tsx';
import { MonitorPage } from './pages/MonitorPage.tsx';
import { OverviewPage } from './pages/OverviewPage.tsx';
import { RoutesPage } from './pages/RoutesPage.tsx';
import { SoundsPage } from './pages/SoundsPage.tsx';
import { SourcesPage } from './pages/SourcesPage.tsx';
import { TestPage } from './pages/TestPage.tsx';
import { UsersPage } from './pages/UsersPage.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A console is read, then acted on. Refetch on focus, do not thrash.
      refetchOnWindowFocus: true,
      staleTime: 5_000,
      retry: (failureCount, error) => {
        const status = (error as { status?: number }).status;
        if (status !== undefined && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/** Nothing behind the login renders until the session question is answered. */
function RequireSession() {
  const { user, isLoading } = useSession();

  if (isLoading) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-base">
        <span className="skeleton h-2 w-32 rounded-full" />
      </div>
    );
  }
  if (!user) return <Navigate to="/anmelden" replace />;
  return <Outlet />;
}

function App() {
  return (
    <Routes>
      <Route path="/anmelden" element={<LoginPage />} />
      <Route element={<RequireSession />}>
        <Route element={<Layout />}>
          <Route index element={<OverviewPage />} />
          <Route path="alarme" element={<AlertsPage />} />
          <Route path="alarme/:alertId" element={<AlertsPage />} />
          <Route path="regeln" element={<RoutesPage />} />
          <Route path="quellen" element={<SourcesPage />} />
          <Route path="quellen/monitore/:monitorId" element={<MonitorPage />} />
          <Route path="kanaele" element={<ChannelsPage />} />
          <Route path="toene" element={<SoundsPage />} />
          <Route path="test" element={<TestPage />} />
          <Route path="benutzer" element={<UsersPage />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <SessionProvider>
          <App />
        </SessionProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
